import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Button, Modal, Form, Input, Select, Space, InputNumber, DatePicker, message, Checkbox, Alert, Segmented, Switch, Row, Col } from "antd";
import { PlusOutlined, FileDoneOutlined } from "@ant-design/icons";
import { API_CONFIG } from "./config";
import { apiFetch } from "../utils/apiFetch";

const { Option } = Select;

const quotationStatuses = ["In Progress", "Approved", "Cancelled", "Expired", "Rejected"];
const invoiceStatuses = ["Unpaid", "Paid", "Overdue"];

export default function Invoices() {
  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteForm] = Form.useForm();
  const [invoiceForm] = Form.useForm();
  const [section, setSection] = useState("invoices");
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("invoices.viewMode");
    return saved === "table" || saved === "cards" ? saved : "cards";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: [] });
  const [isDark, setIsDark] = useState(true);
  const useDarkStyles = true;

  const fetchQuotations = async () => {
    setLoadingQuotes(true);
    const res = await apiFetch(API_CONFIG.quotationsListUrl, { method: "POST" });
    const data = await res.json();
    setQuotations(data.quotations || []);
    setLoadingQuotes(false);
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    const res = await apiFetch(API_CONFIG.invoicesListUrl, { method: "POST" });
    const data = await res.json();
    setInvoices(data.invoices || []);
    setLoadingInvoices(false);
  };

  useEffect(() => {
    fetchQuotations();
    fetchInvoices();
  }, []);

  useEffect(() => {
    localStorage.setItem("invoices.viewMode", viewMode);
  }, [viewMode]);

  const saveQuote = async (values) => {
    await apiFetch(API_CONFIG.quotationsSaveUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(values),
    });
    setQuoteOpen(false);
    fetchQuotations();
  };

  const updateQuoteStatus = async (quote, status) => {
    await apiFetch(API_CONFIG.quotationsStatusUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: quote.id, status }),
    });
    fetchQuotations();
  };

  const openInvoiceForQuote = (quote) => {
    setSelectedQuote(quote);
    invoiceForm.resetFields();
    invoiceForm.setFieldsValue({
      quotationId: quote.id,
      amount: quote.amount,
      taxRate: quote.taxRate ?? 0.15,
      commissionRate: quote.commissionRate ?? 0.05,
      deposit: quote.deposit || 0,
    });
    setInvoiceOpen(true);
  };

  const saveInvoice = async (values) => {
    const payload = {
      ...values,
      dueDate: values.dueDate?.toISOString?.() || values.dueDate,
    };
    await apiFetch(API_CONFIG.invoicesSaveUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    setInvoiceOpen(false);
    message.success("Invoice created");
    fetchInvoices();
  };

  const updateInvoiceStatus = async (invoice, status) => {
    await apiFetch(API_CONFIG.invoicesStatusUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: invoice.id, status }),
    });
    fetchInvoices();
  };

  const quoteColumns = [
    { title: "Quotation ID", dataIndex: "id", key: "id", width: 120 },
    { title: "Lead ID", dataIndex: "leadId", key: "leadId" },
    { title: "Amount", dataIndex: "amount", key: "amount" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, quote) => (
        <Select value={quote.status} onChange={(v) => updateQuoteStatus(quote, v)} size="small">
          {quotationStatuses.map((s) => (
            <Option key={s} value={s}>{s}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, quote) => (
        <Button size="small" icon={<FileDoneOutlined />} onClick={() => openInvoiceForQuote(quote)}>
          Generate Invoice
        </Button>
      ),
    },
  ];

  const invoiceColumns = [
    { title: "Invoice ID", dataIndex: "id", key: "id", width: 120 },
    { title: "Client", dataIndex: "clientName", key: "clientName" },
    { title: "Amount", dataIndex: "amount", key: "amount" },
    { title: "Total Due", dataIndex: "totalDue", key: "totalDue" },
    { title: "Due Date", dataIndex: "dueDate", key: "dueDate" },
    { title: "Recurring", dataIndex: "recurring", key: "recurring", render: (v) => (v ? "Yes" : "No") },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, invoice) => (
        <Select value={invoice.status} onChange={(v) => updateInvoiceStatus(invoice, v)} size="small">
          {invoiceStatuses.map((s) => (
            <Option key={s} value={s}>{s}</Option>
          ))}
        </Select>
      ),
    },
    { title: "Payment Link", dataIndex: "paymentLink", key: "paymentLink" },
  ];

  const toggleFilter = (value) => {
    setFilters((prev) => {
      const next = new Set(prev.status || []);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...prev, status: Array.from(next) };
    });
  };
  const clearFilters = () => setFilters({ status: [] });

  const filteredQuotes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return quotations.filter((q) => {
      const matchesSearch = !term || [q.id, q.leadId].filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
      const matchesStatus = filters.status.length === 0 || filters.status.includes(q.status);
      return matchesSearch && matchesStatus;
    });
  }, [quotations, searchTerm, filters]);

  const filteredInvoices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return invoices.filter((i) => {
      const matchesSearch = !term || [i.id, i.clientName].filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
      const matchesStatus = filters.status.length === 0 || filters.status.includes(i.status);
      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, filters]);

  return (
    <div
      className={`appverse-content table-container tabled ${useDarkStyles ? "luxe-dark" : "luxe-light"}`}
      style={{
        background: isDark ? "#000" : "#fff",
        minHeight: "100vh",
        marginLeft: "var(--sidenav-width, 88px)",
        padding: "0 20px 0 12px",
        position: "relative",
        zIndex: 1002,
      }}
    >
      <Row gutter={0}>
        <Col xs={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24 luxe-dark-bg"
            style={{ margin: 0, position: "relative", zIndex: 1003 }}
            title={<span className="luxe-card-title">Invoicing</span>}
            extra={
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div className="mode-toggle">
                  <Segmented
                    options={[
                      { label: "Quotations", value: "quotes" },
                      { label: "Invoices", value: "invoices" },
                    ]}
                    value={section}
                    onChange={(v) => setSection(v)}
                  />
                </div>
                <div className="mode-toggle">
                  <Segmented
                    options={[
                      { label: "Cards", value: "cards" },
                      { label: "Table", value: "table" },
                    ]}
                    value={viewMode}
                    onChange={(v) => setViewMode(v)}
                  />
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingLeft: 4 }}>
                  <span style={{ color: "#fff8dc", fontWeight: 600 }}>Dark</span>
                  <Switch checked={isDark} onChange={(v) => setIsDark(v)} />
                </div>
              </div>
            }
            bodyStyle={{ background: "#000", borderRadius: "18px" }}
          >
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              {section === "quotes" && (
                <Button type="primary" className="luxe-btn" icon={<PlusOutlined />} onClick={() => setQuoteOpen(true)}>
                  New Quotation
                </Button>
              )}
            </div>

            <Input.Search
              placeholder={section === "quotes" ? "Search quotations by ID or lead" : "Search invoices by ID or client"}
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
              className="luxe-search"
              enterButton={<Button className="luxe-search-btn">Search</Button>}
            />

            <div className="filter-chips">
              <span className="chip-filter">Status</span>
              {(section === "quotes" ? quotationStatuses : invoiceStatuses).map((status) => (
                <span
                  key={status}
                  className={`chip-filter ${filters.status.includes(status) ? "active" : ""}`}
                  onClick={() => toggleFilter(status)}
                >
                  {status}
                </span>
              ))}
              {filters.status.length > 0 && (
                <span className="chip-filter clear" onClick={clearFilters}>Clear</span>
              )}
            </div>

            {section === "invoices" && (
              <Alert
                type="info"
                showIcon
                message="Automation enabled: overdue status and recurring invoice generation happen automatically."
                style={{ margin: "16px 0" }}
              />
            )}

            {viewMode === "cards" && section === "quotes" && (
              <div className="indigent-grid" style={{ marginTop: 20 }}>
                {filteredQuotes.map((q) => (
                  <div key={q.id} className="indigent-card">
                    <div className="indigent-head">
                      <div className="indigent-avatar">Q</div>
                      <div>
                        <div className="indigent-name">{q.id}</div>
                        <div className="indigent-meta">Lead: {q.leadId || "-"}</div>
                      </div>
                    </div>
                    <div className="indigent-tags">
                      <span className="chip dark">Amount: {q.amount}</span>
                      <span className="chip dark">Status: {q.status}</span>
                      <span className="chip dark">Deposit: {q.deposit}</span>
                    </div>
                    <div className="indigent-actions">
                      <Button className="luxe-btn" icon={<FileDoneOutlined />} onClick={() => openInvoiceForQuote(q)}>
                        Generate Invoice
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === "cards" && section === "invoices" && (
              <div className="indigent-grid" style={{ marginTop: 20 }}>
                {filteredInvoices.map((i) => (
                  <div key={i.id} className="indigent-card">
                    <div className="indigent-head">
                      <div className="indigent-avatar">I</div>
                      <div>
                        <div className="indigent-name">{i.id}</div>
                        <div className="indigent-meta">{i.clientName || "Client"} · Due {i.dueDate?.slice?.(0, 10)}</div>
                      </div>
                    </div>
                    <div className="indigent-tags">
                      <span className="chip dark">Amount: {i.amount}</span>
                      <span className="chip dark">Total: {i.totalDue}</span>
                      <span className="chip dark">Status: {i.status}</span>
                      <span className="chip dark">Recurring: {i.recurring ? "Yes" : "No"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === "table" && section === "quotes" && (
              <Table rowKey="id" loading={loadingQuotes} dataSource={filteredQuotes} columns={quoteColumns} className="luxe-table" style={{ marginTop: 16 }} />
            )}
            {viewMode === "table" && section === "invoices" && (
              <Table rowKey="id" loading={loadingInvoices} dataSource={filteredInvoices} columns={invoiceColumns} className="luxe-table" style={{ marginTop: 16 }} />
            )}
          </Card>
        </Col>
      </Row>

      <Modal title="New Quotation" open={quoteOpen} onCancel={() => setQuoteOpen(false)} onOk={() => quoteForm.submit()} okText="Save">
        <Form form={quoteForm} layout="vertical" onFinish={saveQuote}>
          <Form.Item name="leadId" label="Lead ID">
            <Input placeholder="LEAD-XXXX" />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true, message: "Enter amount" }]}>
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="taxRate" label="Tax Rate" initialValue={0.15}>
            <InputNumber style={{ width: "100%" }} step={0.01} />
          </Form.Item>
          <Form.Item name="commissionRate" label="Commission Rate" initialValue={0.05}>
            <InputNumber style={{ width: "100%" }} step={0.01} />
          </Form.Item>
          <Form.Item name="deposit" label="Deposit" initialValue={0}>
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="In Progress">
            <Select>
              {quotationStatuses.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Generate Invoice" open={invoiceOpen} onCancel={() => setInvoiceOpen(false)} onOk={() => invoiceForm.submit()} okText="Create">
        <Form form={invoiceForm} layout="vertical" onFinish={saveInvoice}>
          <Form.Item name="quotationId" label="Quotation ID">
            <Input disabled />
          </Form.Item>
          <Form.Item name="clientName" label="Client Name" rules={[{ required: true, message: "Enter client name" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true, message: "Enter amount" }]}>
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="deposit" label="Deposit">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="taxRate" label="Tax Rate">
            <InputNumber style={{ width: "100%" }} step={0.01} />
          </Form.Item>
          <Form.Item name="commissionRate" label="Commission Rate">
            <InputNumber style={{ width: "100%" }} step={0.01} />
          </Form.Item>
          <Form.Item name="paymentMethod" label="Payment Method" initialValue="card">
            <Select>
              <Option value="card">Card</Option>
              <Option value="debit">Debit Order</Option>
              <Option value="wallet">Wallet</Option>
            </Select>
          </Form.Item>
          <Form.Item name="recurring" valuePropName="checked">
            <Checkbox>Recurring payment invoice</Checkbox>
          </Form.Item>
          <Form.Item name="recurringIntervalDays" label="Recurring Interval (days)" initialValue={30}>
            <InputNumber style={{ width: "100%" }} min={7} max={365} />
          </Form.Item>
          <Form.Item name="dueDate" label="Due Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="paymentLink" label="Payment Link">
            <Input placeholder="https://pay..." />
          </Form.Item>
          <Form.Item name="supplierThirdParty" valuePropName="checked">
            <Checkbox>Third-party supplier invoice</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
